type BillingCycle = 'monthly' | 'yearly';
type SelectablePlanCode =
    | 'SOLO_199'
    | 'SOLO_399'
    | 'BASIC'
    | 'SOLO_999'
    | 'SOLO_1299'
    | 'TEAM_1499'
    | 'TEAM_2999'
    | 'PRO'
    | 'TEAM_9999'
    | 'TEAM_49999';

interface ResolvePlanChangeIntentInput {
    currentPlanCode?: string | null;
    currentCycle?: string | null;
    targetPlanCode: SelectablePlanCode;
    targetCycle: BillingCycle;
    renew: boolean;
    isExpired: boolean;
}

interface PlanChangeIntent {
    isSwitchingCycle: boolean;
    isSamePlanCycleUpgrade: boolean;
    isUpgradingToPro: boolean;
    isDowngradingToBasic: boolean;
    isDowngradingSoloVariant: boolean;
    isSwitchingTeamVariant: boolean;
    isUpgradingTeamVariant: boolean;
    isDowngradingTeamVariant: boolean;
    isImmediateUpgrade: boolean;
    isDeferredPlanChange: boolean;
}

const normalizeCode = (value?: string | null): string => (value || '').trim().toUpperCase();
const isTeamPlanCode = (value?: string | null): boolean => {
    const normalizedCode = normalizeCode(value);
    return normalizedCode === 'TEAM_1499'
        || normalizedCode === 'TEAM_2999'
        || normalizedCode === 'PRO'
        || normalizedCode === 'TEAM_9999'
        || normalizedCode === 'TEAM_49999';
};
const isSoloPlanCode = (value?: string | null): boolean => {
    const normalizedCode = normalizeCode(value);
    return normalizedCode === 'SOLO_199'
        || normalizedCode === 'SOLO_399'
        || normalizedCode === 'BASIC'
        || normalizedCode === 'SOLO_999'
        || normalizedCode === 'SOLO_1299';
};

const PLAN_CHANGE_RANK: Record<string, number> = {
    SOLO_199: 10,
    SOLO_399: 11,
    BASIC: 12,
    SOLO_999: 13,
    SOLO_1299: 14,
    TEAM_1499: 20,
    TEAM_2999: 30,
    PRO: 40,
    TEAM_9999: 50,
    TEAM_49999: 60,
};

const getPlanChangeRank = (planCode: string): number | null => {
    return PLAN_CHANGE_RANK[planCode] ?? null;
};

export type PlanChangeDirection = 'upgrade' | 'downgrade' | 'same' | 'unknown';

export const resolvePlanChangeDirection = (
    currentPlanCode?: string | null,
    targetPlanCode?: string | null,
): PlanChangeDirection => {
    const currentCode = normalizeCode(currentPlanCode);
    const targetCode = normalizeCode(targetPlanCode);
    if (!currentCode || !targetCode) return 'unknown';
    if (currentCode === targetCode) return 'same';

    const currentRank = getPlanChangeRank(currentCode);
    const targetRank = getPlanChangeRank(targetCode);
    if (currentRank === null || targetRank === null) return 'unknown';
    return targetRank < currentRank ? 'downgrade' : 'upgrade';
};

const normalizeCycle = (value?: string | null): BillingCycle | null => {
    if (value === 'monthly' || value === 'yearly') {
        return value;
    }
    return null;
};

export const resolvePlanChangeIntent = ({
    currentPlanCode,
    currentCycle,
    targetPlanCode,
    targetCycle,
    renew,
    isExpired,
}: ResolvePlanChangeIntentInput): PlanChangeIntent => {
    const currentCode = normalizeCode(currentPlanCode);
    const targetCode = normalizeCode(targetPlanCode);
    const currentRank = getPlanChangeRank(currentCode);
    const targetRank = getPlanChangeRank(targetCode);
    const normalizedCurrentCycle = normalizeCycle(currentCycle);
    const normalizedTargetCycle = normalizeCycle(targetCycle);
    const isCurrentPlan = Boolean(currentCode) && currentCode === targetCode;
    const isSwitchingCycle = !renew
        && !isExpired
        && isCurrentPlan
        && normalizedCurrentCycle !== null
        && normalizedTargetCycle !== null
        && normalizedCurrentCycle !== normalizedTargetCycle;
    const isSamePlanCycleUpgrade = isSwitchingCycle
        && normalizedCurrentCycle === 'monthly'
        && normalizedTargetCycle === 'yearly';
    const isUpgradingToPro = !renew
        && !isExpired
        && isTeamPlanCode(targetCode)
        && Boolean(currentCode)
        && !isTeamPlanCode(currentCode);
    const isDowngradingToBasic = !renew
        && !isExpired
        && Boolean(currentCode)
        && isSoloPlanCode(targetCode)
        && currentRank !== null
        && targetRank !== null
        && currentRank > targetRank;
    const isSwitchingSoloVariant = !renew
        && !isExpired
        && isSoloPlanCode(currentCode)
        && isSoloPlanCode(targetCode)
        && currentCode !== targetCode
        && currentRank !== null
        && targetRank !== null;
    const isUpgradingSoloVariant = isSwitchingSoloVariant
        && currentRank !== null
        && targetRank !== null
        && currentRank < targetRank;
    const isDowngradingSoloVariant = isSwitchingSoloVariant
        && currentRank !== null
        && targetRank !== null
        && currentRank > targetRank;
    const isSwitchingTeamVariant = !renew
        && !isExpired
        && isTeamPlanCode(currentCode)
        && isTeamPlanCode(targetCode)
        && currentCode !== targetCode
        && currentRank !== null
        && targetRank !== null;
    const isUpgradingTeamVariant = isSwitchingTeamVariant
        && currentRank !== null
        && targetRank !== null
        && currentRank < targetRank;
    const isDowngradingTeamVariant = isSwitchingTeamVariant
        && currentRank !== null
        && targetRank !== null
        && currentRank > targetRank;
    const isImmediateUpgrade = isSamePlanCycleUpgrade || isUpgradingToPro || isUpgradingTeamVariant || isUpgradingSoloVariant;
    const isDeferredPlanChange = (isSwitchingCycle && !isSamePlanCycleUpgrade)
        || isDowngradingToBasic
        || isDowngradingTeamVariant;

    return {
        isSwitchingCycle,
        isSamePlanCycleUpgrade,
        isUpgradingToPro,
        isDowngradingToBasic,
        isDowngradingSoloVariant,
        isSwitchingTeamVariant,
        isUpgradingTeamVariant,
        isDowngradingTeamVariant,
        isImmediateUpgrade,
        isDeferredPlanChange,
    };
};
